import type { FC } from "react";
import type { EdgeProps } from "@xyflow/react";
import {
  CONNECTOR_ZONE,
  EDGE_CORNER_RADIUS,
  FAN_EDGE_GAP,
} from "../graph/constants";

// Mirrors legacy StyledConnectorArrow (Branches/style.tsx): 4px + 4px
// transparent side borders (= 8px wide) and a 5px solid top border (= 5px tall).
const ARROW_WIDTH = 8;
const ARROW_HEIGHT = 5;

/** Dashed connector from a Conditional Logic block card down to one branch
 *  header: vertical stub, horizontal bus (rounded corner), vertical stub
 *  ending in a triangle arrow. Replica of StyledBranchRow/StyledConnectorArrow
 *  CSS (components/ActionGroup/Branches/style.tsx). Sibling edges overlap on
 *  the bus segment, which is fine — same color, same dash pattern. */
export const BranchFanEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
}) => {
  const startY = sourceY + FAN_EDGE_GAP;
  const arrowTipY = targetY - FAN_EDGE_GAP;
  const busY = sourceY + CONNECTOR_ZONE / 2;
  const r = Math.min(EDGE_CORNER_RADIUS, Math.abs(targetX - sourceX) / 2);
  const dir = Math.sign(targetX - sourceX); // -1 left, 0 straight, +1 right
  const stubEndY = arrowTipY - ARROW_HEIGHT;

  const path =
    dir === 0
      ? `M ${sourceX},${startY} L ${sourceX},${stubEndY}`
      : [
          `M ${sourceX},${startY}`,
          `L ${sourceX},${busY - r}`,
          `Q ${sourceX},${busY} ${sourceX + dir * r},${busY}`,
          `L ${targetX - dir * r},${busY}`,
          `Q ${targetX},${busY} ${targetX},${busY + r}`,
          `L ${targetX},${stubEndY}`,
        ].join(" ");

  return (
    <g id={id}>
      <path
        d={path}
        fill="none"
        stroke="rgba(0, 0, 0, 0.5)"
        strokeWidth={1}
        strokeDasharray="4 4"
      />
      <path
        d={`M ${targetX - ARROW_WIDTH / 2},${stubEndY} L ${
          targetX + ARROW_WIDTH / 2
        },${stubEndY} L ${targetX},${arrowTipY} Z`}
        fill="rgba(0, 0, 0, 0.5)"
      />
    </g>
  );
};
